import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import * as Icons from '@mui/icons-material';
import { DataTable } from '../../../components/DataTable';
import { Link } from '../../../components/Link';
import * as Entities from '../../../constants/Entities';
import { entityLocation } from '../../../utils/entity';
import { TooltipIcon } from '../../../components/TooltipIcon';

interface Attribute {
  id: string;
  name?: string | null;
  isGlobal?: boolean | null;
  mappingAttributeDeviceModelAttributeSets?: Array<{
    id: string;
  } | null> | null;
}

interface AttributeListProps {
  attributes: Attribute[];
  onAdd?: () => void;
  onChangePage: (page: number) => void;
  onChangePageSize: (pageSize: number) => void;
  page: number;
  pageSize: number;
  count: number;
  onSearchSubmit: (query: string) => void;
  searchValue: string;
  loading: boolean;
}

export const AttributeList: React.FC<AttributeListProps> = ({
  attributes,
  onAdd,
  onChangePageSize,
  onChangePage,
  page,
  pageSize,
  count,
  onSearchSubmit,
  searchValue,
  loading,
}) => {
  const { t } = useTranslation(['attributes', 'general']);

  const headers = [
    {
      key: 'name',
      value: t('general:fields.name'),
    },
    {
      key: 'attributeCount',
      value: t('attributes:list.headers.attributeCount'),
    },
    {
      key: 'attributeType',
      value: (
        <TooltipIcon
          text={t('attributes:list.headers.attributeType')}
          tootltipText={t('attributes:list.attributeTypeTooltip')}
        />
      ),
    },
    {
      key: 'actions',
      value: '',
    },
  ];

  const rows = attributes.map((attribute) => ({
    key: `${attribute.id}-row`,
    cells: [
      {
        key: `${attribute.id}-cell-name`,
        element: (
          <Link to={entityLocation(Entities.ATTRIBUTE, attribute.id)}>
            {attribute.name || attribute.id}
          </Link>
        ),
      },
      {
        key: `${attribute.id}-cell-attributeCount`,
        element:
          attribute.mappingAttributeDeviceModelAttributeSets?.length ?? 0,
      },
      {
        key: `${attribute.id}-cell-attributeType`,
        element: attribute.isGlobal
          ? t('general:entityType.global')
          : t('general:entityType.local'),
      },
      {
        key: `${attribute.id}-cell-actions`,
        element: null,
      },
    ],
  }));

  return (
    <DataTable
      actions={
        onAdd && (
          <Button
            color="secondary"
            variant="outlined"
            size="large"
            startIcon={<Icons.Add />}
            onClick={onAdd}
          >
            {t('attributes:list.actions.addButton')}
          </Button>
        )
      }
      page={page}
      pageSize={pageSize}
      count={count}
      onChangePage={onChangePage}
      onChangePageSize={onChangePageSize}
      headers={headers}
      rows={rows}
      showPagination
      showSearch
      onSearchSubmit={onSearchSubmit}
      searchValue={searchValue}
      loading={loading}
    />
  );
};
